import InfoSection from '../components/info_section';
import ScrollButton from '../components/scroll_button';
import Layout from '../components/layout';
import { useRouter } from 'next/router';
import { useTranslation } from 'next-i18next';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import styles from '../styles/veeam.module.scss';
import Link from 'next/link';
import Image from 'next/image';
import { chooseVeeamReasonsList, continuousInnovationList, exploreOptionsList } from '../constants/veeam';
import FlexibleBackupsIcon from '../public/services/veeam/icons/flexibleBackups.svg';
import SecureCloudStorageIcon from '../public/services/veeam/icons/secureCloudStorage.svg';
import KVMCompatibleIcon from '../public/services/veeam/icons/kvmCompatible.svg';
import ComprehensiveDataProtectionIcon from '../public/services/veeam/icons/comprehensiveDataProtection.svg';
import CrossLocationRecoveryIcon from '../public/services/veeam/icons/crossLocationRecovery.svg';
import EnterpriseGradeReliabilityIcon from '../public/services/veeam/icons/enterpriseGradeReliability.svg';
import RegularUpdatesIcon from '../public/services/veeam/icons/regularUpdates.svg';
import ExpertSupportIcon from '../public/services/veeam/icons/expertSupport.svg';
import Slider from '../components/slider/slider'
import computeImage from '../svgs/relatedServices/compute.svg'
import StartBusiness from '../components/startBusiness';

const reasonsIcons = {
  flexibleBackups: FlexibleBackupsIcon,
  secureCloudStorage: SecureCloudStorageIcon,
  kvmCompatible: KVMCompatibleIcon,
  comprehensiveDataProtection: ComprehensiveDataProtectionIcon,
  crossLocationRecovery: CrossLocationRecoveryIcon,
  enterpriseGradeReliability: EnterpriseGradeReliabilityIcon,
}

const innovationIcons = {
  regularUpdates: RegularUpdatesIcon,
  expertSupport: ExpertSupportIcon,
}

export default function Veeam() {
  const router = useRouter();
  const { t } = useTranslation('veeam');

  const documentationLink = 'https://help.icdc.io/compute/';
  const documentationHref = `${documentationLink}${router.locale}/Welcome.html`;

  const exploreOptionsData = exploreOptionsList.map((element, index) => (
    <div key={index} className={styles.exploreOptions__item} style={{ backgroundImage: `url(${element.imageUrl})` }}>
      <h3>{t(element.title)}</h3>
      <p>{t(element.description)}</p>
    </div>
  ))

  return (
    <Layout title={t('title')} lang={router.locale} route={router.route} isHeader isFooter>
      <ScrollButton scrollStepInPx='50' delayInMs='16.66' />
      <InfoSection title={t('veeamTitle')} description='veeamDescription' documentationLink={documentationLink} lang={router.locale} />

      <section className={styles.chooseVeeam}>
        <h2>{t('chooseVeeam.title')}</h2>
        <div className={styles.chooseVeeam__items}>
          {chooseVeeamReasonsList.map((element, index) => (
            <div key={index} className={styles.chooseVeeam__item}>
              <Image src={reasonsIcons[element.iconUrl]} alt={element.iconUrl} loading='lazy' />
              <h3>{t(element.title)}</h3>
              <p>{t(element.description)}</p>
            </div>
          ))}
        </div>
      </section>

      <section className={styles.exploreOptions}>
        <h2>{t('exploreOptions.title')}</h2>
        <p>{t('exploreOptions.description')}</p>

        <div className={styles.exploreOptions__items}>
          {exploreOptionsData}
        </div>
        <div className={styles.exploreOptions__slider} >
          <Slider dataSlider={exploreOptionsData} />
        </div>
        <h3>{t('exploreOptions.advancedBackup.title')}</h3>
        <p>{t('exploreOptions.advancedBackup.description')}</p>
      </section>

      <section className={styles.continuousInnovation}>
        <h2>{t('continuousInnovation.title')}</h2>
        <div className={styles.content}>
          <div className={styles.content__items}>
            {continuousInnovationList.map((element, index) => (
              <div key={index} className={styles.content__items__item}>
                <Image src={innovationIcons[element.iconUrl]} alt={element.iconUrl} />
                <h3>{t(element.title)}</h3>
                <p>{t(element.description)}</p>
              </div>
            ))}
            <a rel='noopener' href={documentationHref} target='_blank' className={styles.lgBtn}>
              <button className='btn outlined'>{t('continuousInnovation.checkDocs')}</button>
            </a>
          </div>
          <div className={styles.imageWrapper}>
            <Image src='/services/veeam/continuousInnovation.webp' alt='Continuous Innovation and Support' width={545} height={408} unoptimized loading='lazy' />
          </div>
          <a rel='noopener' href={documentationHref} target='_blank' className={styles.smBtn}>
            <button className='btn outlined'>{t('continuousInnovation.checkDocs')}</button>
          </a>
        </div>
      </section>

      <StartBusiness />

      <section className={styles.relatedServices}>
        <h3>{t('relatedServices.title')}</h3>
        <Link href='/compute'>
          <div className={styles.relatedServices__item}>
            <Image src={computeImage} alt={t('relatedServices.compute')} />
            <p>{t('relatedServices.compute')}</p>
          </div>
        </Link>
      </section>
    </Layout>
  )
};

export const getStaticProps = async ({ locale }) => ({
  props: {
    ...await serverSideTranslations(locale, ['veeam', 'common']),
  },
});
